import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  Analyst,
  AnalystRoleEnum,
  UsersService,
} from '@compumark/analyst-selection-tool-client';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-existing-users-list',
  standalone: false,
  templateUrl: './existing-users-list.component.html',
  styleUrls: ['./existing-users-list.component.scss'],
})
export class ExistingUsersListComponent implements OnInit, OnChanges {
  @Output() closeUsersTable = new EventEmitter<void>();
  @Input() refreshUserData = false;

  displayedColumns = [
    'userName',
    'firstName',
    'lastName',
    'email',
    'teamLead',
    'userRoles',
  ];

  dataSource: Analyst[] = [];

  private readonly ROLE_ORDER: AnalystRoleEnum[] = [
    'TEAM_LEAD',
    'WORKFLOW_COORDINATOR',
    'QUALITY_ANALYST',
    'ANALYST',
  ];

  constructor(private usersService: UsersService) {}

  ngOnInit(): void {
    this.loadAnalysts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.refreshUserData && !changes.refreshUserData.firstChange) {
      this.loadAnalysts();
    }
  }

  loadAnalysts(): void {
    this.usersService
      .getAllAnalysts()
      .pipe(untilDestroyed(this))
      .subscribe((analysts) => {
        this.dataSource = Array.isArray(analysts)
          ? [...analysts].sort((a, b) =>
              (a.lastName || '').localeCompare(b.lastName || '', 'en', {
                sensitivity: 'base',
              }),
            )
          : [];
      });
  }

  analystRole(role: AnalystRoleEnum): string {
    switch (role) {
      case 'TEAM_LEAD':
        return 'Team lead';
      case 'WORKFLOW_COORDINATOR':
        return 'Workflow coordinator';
      case 'QUALITY_ANALYST':
        return 'Quality analyst';
      case 'ANALYST':
        return 'Analyst';
      default:
        return role.toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
    }
  }

  formatRoles(r?: string[]): string {
    if (!r?.length) {
      return '';
    }

    const normalize = (role: string) => role.toUpperCase().replace(/ /g, '_');

    const knownRoles = r.filter((role) =>
      this.ROLE_ORDER.includes(normalize(role) as AnalystRoleEnum),
    );

    const unknownRoles = r.filter(
      (role) => !this.ROLE_ORDER.includes(normalize(role) as AnalystRoleEnum),
    );

    const sortedKnown = [...knownRoles].sort(
      (a, b) =>
        this.ROLE_ORDER.indexOf(normalize(a) as AnalystRoleEnum) -
        this.ROLE_ORDER.indexOf(normalize(b) as AnalystRoleEnum),
    );

    return [...sortedKnown, ...unknownRoles]
      .map(
        (role) => this.analystRole(normalize(role) as AnalystRoleEnum) || role,
      )
      .join(', ');
  }
}
